'use client'

import Link, { type LinkProps } from 'next/link'
import { useMemo } from 'react'

import { useLocale } from '@src/i18n/LocaleProvider'
import {
  STREAMING_APP_PREFIX,
  getDefaultLocale,
  getLocaleFromCode,
  isAppPath,
  isLocaleAllowedOnPath,
  isValidLocalePath,
} from '@src/i18n/locale.util'
import type { LocaleConfig } from '@src/i18n/locales'
import { AuthRoutes } from '@src/utils/constants.util'
import { transformExternalBODiLink } from '@src/utils/externalBodiLink'
import { isExternalBODiLink } from '@src/utils/externalBodiLink'
import { log } from '@src/utils/log.util'

type LocalizedLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps &
  React.RefAttributes<HTMLAnchorElement> & {
    children?: React.ReactNode
    targetCountry?: string
    targetLanguage?: string
    href: string
    useNextLink?: boolean
  }

const getTransformedHref = ({
  targetCountry,
  targetLanguage,
  lang,
  country,
  href,
  locale,
}: {
  targetCountry?: string
  targetLanguage?: string
  lang: string
  country: string
  href: string
  locale?: LocaleConfig
}) => {
  if (isExternalBODiLink(href)) {
    try {
      return transformExternalBODiLink(href, locale)
    } catch (error) {
      log.error('Error transforming external BODi link', { error })
      return href
    }
  }

  if (Object.values(AuthRoutes).some((route) => href.startsWith(route))) {
    return href
  }

  if (href.startsWith('http')) {
    return href
  }

  if (href.startsWith('#')) {
    return href
  }

  if (isValidLocalePath(href)) {
    return href
  }

  // Determine the target locale
  let targetLocaleCountry = targetCountry ?? country
  let targetLocaleLang = targetLanguage ?? lang

  // Build the locale code and get config
  const targetLocaleCode = `${targetLocaleLang}-${targetLocaleCountry.toUpperCase()}`

  // For /app/* hrefs the locale is injected after /app, not at the root
  const hrefIsAppPath = isAppPath(href)
  const hrefForLocaleCheck = hrefIsAppPath ? href.slice(STREAMING_APP_PREFIX.length) : href

  // Build the full path with current locale to check if the locale is allowed on this path
  const fullPath = `/${targetLocaleCountry}/${targetLocaleLang}${hrefForLocaleCheck}`

  // If the locale is not allowed on this path, use the fallback
  if (!isLocaleAllowedOnPath(targetLocaleCode, fullPath)) {
    const targetLocaleConfig = getLocaleFromCode(targetLocaleCode)
    const fallbackLocaleConfig = getLocaleFromCode(targetLocaleConfig.fallbackLocale)

    targetLocaleCountry = fallbackLocaleConfig.countryCodeLowercase
    targetLocaleLang = fallbackLocaleConfig.lang
  }

  const localeSegment = `/${targetLocaleCountry}/${targetLocaleLang}`
  const localePrefix = hrefIsAppPath ? `${STREAMING_APP_PREFIX}${localeSegment}` : localeSegment

  const defaultCountry = getDefaultLocale().countryCodeLowercase
  const isDefaultHomepage =
    href === '/' &&
    (targetCountry ? targetLocaleCountry === defaultCountry : country === defaultCountry)

  if (isDefaultHomepage) {
    return href
  }

  return `${localePrefix}${hrefForLocaleCheck}`
}

export const LocalizedLink = ({
  targetLanguage,
  targetCountry,
  href,
  children,
  useNextLink = true,
  ...props
}: LocalizedLinkProps) => {
  const { locale } = useLocale()
  const isSameOrigin = !href.startsWith('http')
  const isHashNavigation = href.startsWith('#')
  const transformedHref = useMemo(
    () =>
      getTransformedHref({
        targetCountry,
        targetLanguage,
        lang: locale.lang,
        country: locale.countryCodeLowercase,
        href,
        locale,
      }),
    [locale, href, targetCountry, targetLanguage]
  )

  if (useNextLink && isSameOrigin && !isHashNavigation) {
    return (
      <Link href={transformedHref} {...props}>
        {children}
      </Link>
    )
  }

  return (
    <a href={transformedHref} {...props}>
      {children}
    </a>
  )
}
